import React, { HTMLAttributes } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Pagination } from "swiper"
import { CSS_VARIABLES } from "./cssVariablesSwiper"

import "swiper/scss"
import "swiper/scss/navigation"
import "swiper/scss/pagination"
import "./tabbed-content-slider.module.scss"

import {
  ContentBlockElements,
  TabContentBlockItem,
} from "../tabbed-content-block-types"

import { NextArrow, PrevArrow } from "./SliderArrow"
import { ServiceElements } from "components/service-comparison/service-types"
import { SingleService } from "components/service-comparison/SingleService"
import { TextAndImageContentBlock } from "components/content-blocks/TextAndImageContentBlock"
import { useSlider } from "components/testimonial/hooks/useSlider"

type DivWithoutClassName = Omit<HTMLAttributes<HTMLDivElement>, "className">

interface TabbedContentSliderProps extends DivWithoutClassName {
  content: TabContentBlockItem[]
}

export const TabbedContentSlider = ({ content }: TabbedContentSliderProps) => {
  const {
    activeIndex,
    handleNextSlide,
    handlePrevSlide,
    handleSetActiveIndex,
    handleSetSwiperInstance,
  } = useSlider()

  const generateList = () => {
    let list = [] as string[]

    content.map(({ elements }) => {
      if (!elements) return
      const { tab_title } = elements

      if (tab_title) list.push(tab_title?.value)
    })

    return list
  }

  const SwiperPaginationLabels = () => {
    const tabTitles = generateList()

    return (
      <div className="swiper-pagination-labels">
        {tabTitles.map((label, i) => {
          const activeLabelClass =
              activeIndex === i ? " swiper-pagination-label--active" : "",
            labelClasses = ["swiper-pagination-label", activeLabelClass]
              .filter(c => c)
              .join(" ")

          return (
            <span className={labelClasses} key={i}>
              {label}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className="fr-tabbed-gallery-slider">
      <SwiperPaginationLabels />
      <Swiper
        autoHeight
        centeredSlides
        grabCursor
        loop
        className="container"
        modules={[Navigation, Pagination]}
        onActiveIndexChange={swiper => {
          handleSetActiveIndex(swiper.realIndex)
        }}
        onSwiper={handleSetSwiperInstance}
        pagination={{ clickable: true }}
        slidesPerView="auto"
        spaceBetween={0}
        style={CSS_VARIABLES}
      >
        {content.map(({ elements, id }) => {
          const contentItemElements =
            elements?.content_block?.modular_content[0]?.elements
          if (!contentItemElements) return null

          if (contentItemElements.hasOwnProperty("service_features")) {
            const {
              button,
              image,
              flag,
              icon,
              pests_covered,
              price_term,
              price_tiers,
              pricing_disclaimer,
              service_features,
            } = contentItemElements as ServiceElements

            return (
              <SwiperSlide key={id}>
                <SingleService
                  isTabbedContent
                  ctaButton={button?.value}
                  disclaimer={pricing_disclaimer?.value}
                  featuredImage={image?.value[0]}
                  flag={flag?.value}
                  pestsCovered={pests_covered}
                  priceTerm={price_term?.value[0].codename}
                  priceTiers={price_tiers?.value[0]}
                  serviceFeatures={service_features}
                  serviceIcon={icon?.value[0]}
                />
              </SwiperSlide>
            )
          }

          const { buttons, content, image, style_lists_as_grid } =
            contentItemElements as ContentBlockElements

          return (
            <SwiperSlide key={id}>
              <TextAndImageContentBlock
                buttons={buttons?.value}
                content={content}
                hasBlockPadding={false}
                hasContainerClassName={false}
                image={image?.value[0]}
                key={id}
                styleListsAsGrid={
                  style_lists_as_grid?.value[0]?.codename === "yes"
                }
              />
            </SwiperSlide>
          )
        })}
        <PrevArrow
          onClick={() => handlePrevSlide()}
          prefix="fr-tabbed-content-slider"
        />
        <NextArrow
          onClick={() => handleNextSlide()}
          prefix="fr-tabbed-content-slider"
        />
      </Swiper>
    </div>
  )
}
